import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";

export type AccessHistoryTab = "1" | "2" | "3";

export const ACCESS_HISTORY_TABS: {
  value: AccessHistoryTab;
  label: string;
  /** Rótulo compacto — cabe nas 3 colunas sem scroll lateral */
  shortLabel: string;
  /** Texto explicativo exibido abaixo das abas */
  description: string;
}[] = [
  {
    value: "1",
    label: "Histórico de acessos",
    shortLabel: "Quem acessou",
    description: "Visualize quem acessou seu registro.",
  },
  {
    value: "2",
    label: "Meus acessos",
    shortLabel: "Meus acessos",
    description: "Registros que você visualizou.",
  },
  {
    value: "3",
    label: "Documentos visualizados",
    shortLabel: "Documentos",
    description: "Organizações que visualizaram seus documentos clínicos.",
  },
];

export const TAB_TITLES: Record<AccessHistoryTab, string> = {
  "1": "Histórico de acessos",
  "2": "Meus acessos",
  "3": "Documentos visualizados",
};

export const TAB_DESCRIPTIONS: Record<AccessHistoryTab, string> = {
  "1": ACCESS_HISTORY_TABS[0].description,
  "2": ACCESS_HISTORY_TABS[1].description,
  "3": ACCESS_HISTORY_TABS[2].description,
};

interface AccessHistorySegmentedTabsProps {
  value: AccessHistoryTab;
  onChange: (tab: AccessHistoryTab) => void;
  /** Menos padding vertical (modal Ver tudo) */
  compact?: boolean;
}

/**
 * Seletor de abas em pill — três opções visíveis lado a lado, sem scroll horizontal.
 */
export function AccessHistorySegmentedTabs({
  value,
  onChange,
  compact = false,
}: AccessHistorySegmentedTabsProps) {
  const theme = useTheme();
  const isMobileLayout = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      role="tablist"
      aria-label="Filtrar histórico de acessos"
      sx={{
        display: "flex",
        gap: 0.5,
        width: "100%",
        bgcolor: (t) => t.palette.action.hover,
        borderRadius: 2.5,
        p: 0.5,
        mb: compact ? 1 : 1.25,
      }}
    >
      {ACCESS_HISTORY_TABS.map((tab) => {
        const selected = value === tab.value;
        const displayLabel = isMobileLayout ? tab.shortLabel : tab.label;

        return (
          <Box
            key={tab.value}
            role="tab"
            aria-selected={selected}
            component="button"
            type="button"
            onClick={() => onChange(tab.value)}
            sx={{
              flex: "1 1 0",
              minWidth: 0,
              border: "none",
              cursor: "pointer",
              borderRadius: 2,
              py: compact ? 0.65 : 0.85,
              px: isMobileLayout ? 0.35 : 0.75,
              fontFamily: "inherit",
              fontSize: isMobileLayout ? "0.6875rem" : "0.8125rem",
              fontWeight: 700,
              lineHeight: 1.25,
              textAlign: "center",
              transition: "background-color 0.18s ease, box-shadow 0.18s ease, color 0.18s ease",
              minHeight: compact ? 36 : 44,
              bgcolor: selected ? "background.paper" : "transparent",
              color: selected ? "primary.main" : "text.secondary",
              boxShadow: selected ? "0 1px 4px rgba(0,0,0,0.1)" : "none",
              WebkitTapHighlightColor: "transparent",
              "&:active": {
                transform: "scale(0.98)",
              },
            }}
          >
            {displayLabel}
          </Box>
        );
      })}
    </Box>
  );
}

/** Texto auxiliar da aba ativa — explica o conteúdo de cada seção. */
export function AccessHistoryTabHelperText({
  tab,
  compact = false,
}: {
  tab: AccessHistoryTab;
  compact?: boolean;
}) {
  return (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{
        display: "block",
        px: 0.25,
        mb: compact ? 0.75 : 1,
        fontSize: "0.75rem",
        lineHeight: 1.4,
        fontWeight: 500,
      }}
    >
      {TAB_DESCRIPTIONS[tab]}
    </Typography>
  );
}
